import { Flex, Padding, Typography } from 'UI';
import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { THEME_TYPE } from '@interfaces';
import {
	FontWeight,
	FONT_WEIGHT,
	TextAlign,
	TextColor,
	TEXT_ALIGN,
	TEXT_COLOR,
} from 'styles/constants';

interface Props {
	textColor?: TEXT_COLOR | TextColor | string;
	textSize?: number;
	textWeight?: FONT_WEIGHT | FontWeight | number;
	textAlign?: TEXT_ALIGN | TextAlign;
	postfix?: string;
	postfixColor?: TEXT_COLOR | TextColor | string;
	postfixSize?: number;
	postfixWeight?: FONT_WEIGHT | FontWeight | number;
	prefix?: string;
	prefixColor?: TEXT_COLOR | TextColor | string;
	prefixSize?: number;
	prefixWeight?: FONT_WEIGHT | FontWeight | number;
	placeholder?: string;
	placeholderColor?: TEXT_COLOR | TextColor | string;
	errorText?: string;
	errorTextColor?: TEXT_COLOR | TextColor | string;
	errorTextSize?: number;
	errorTextWeight?: FONT_WEIGHT | FontWeight | number;
	errorTextAlign?: TEXT_ALIGN | TextAlign;
	onChange?: (value?: string, isValid?: boolean) => void;
	validateCallback?: (value?: string) => boolean;
	borderColor?: string;
	borderActiveColor?: string;
	borderWidth?: string;
	maxlength?: number;
	max?: number;
	type?: 'number' | 'text' | 'email' | 'date';
	autocomplete?: boolean;
	autoFocus?: boolean;
	id?: string;
	name?: string;
	value?: any | undefined;
	Ref?: ((instance: HTMLInputElement | null) => void) | React.RefObject<HTMLInputElement> | null;
}

const Container = styled.div<Props>`
	position: relative;
	cursor: text;
	border-radius 5px;
	padding: 0 16px;
	background: ${({ theme }) => theme.colors.bg2};
	display: flex;
	height: 44px;
	box-sizing: border-box;
	overflow: hidden;

	&:hover {
		box-shadow: inset 0px 0px 0px 1px #9580F7;
	}
`;

const Wrapper = styled.div<Props>`
	display: flex;
	align-items: center;
	height: 100%;
`;

const StyledInput = styled.input<Omit<Props, 'onChange'>>`
	width: 100%;
	color: ${({ theme, textColor }) => {
		if (!textColor) return theme.colors.text1 || '#000';
		return Object.values(TEXT_COLOR).includes(textColor as TEXT_COLOR)
			? theme.colors[textColor as TextColor]
			: textColor;
	}};
	font-size: ${({ textSize }) => textSize ?? 14}px;
	text-align: ${({ textAlign }) => textAlign ?? 'left'};
	font-weight: ${({ textWeight, theme }) => {
		if (!textWeight) return 'normal';
		return isNaN(+textWeight) ? theme.fontWeight[textWeight as FontWeight] : textWeight;
	}};

	&::placeholder {
		color: ${({ theme, placeholderColor }) => {
			if (!placeholderColor) return theme.colors.text4 || '#ccc';
			return Object.values(TEXT_COLOR).includes(placeholderColor as TEXT_COLOR)
				? theme.colors[placeholderColor as TextColor]
				: placeholderColor;
		}};
	}

	&::after {
		position: absolute;
		left: 0;
		color: ${({ theme, placeholderColor }) => {
			if (!placeholderColor) return theme.colors.text4 || '#ccc';
			return Object.values(TEXT_COLOR).includes(placeholderColor as TEXT_COLOR)
				? theme.colors[placeholderColor as TextColor]
				: placeholderColor;
		}};
		content: attr(placeholder);
		width: 100%;
		z-index: var(--sq-index);
		background: ${({ theme }) => theme.colors.bg2};
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		pointer-events: none;
	}

	&[type='date']::-webkit-calendar-picker-indicator {
		color-scheme: ${({ theme }) => (theme.type === THEME_TYPE.DARK ? 'dark' : 'light')};
	}
`;

export const InputDate: FC<Props> = props => {
	const {
		prefix,
		prefixColor = TEXT_COLOR.TEXT1,
		prefixSize = 26,
		prefixWeight = FONT_WEIGHT.MEDIUM,
		postfix,
		postfixColor = TEXT_COLOR.TEXT1,
		postfixSize = 26,
		postfixWeight = FONT_WEIGHT.MEDIUM,
		placeholder,
		textColor = TEXT_COLOR.TEXT1,
		textSize = 26,
		textWeight = FONT_WEIGHT.MEDIUM,
		textAlign,
		errorText,
		errorTextColor,
		errorTextSize = 12,
		errorTextWeight,
		errorTextAlign = 'center',
		type = 'text',
		max,
		onChange,
		validateCallback,
		maxlength,
		autoFocus,
		Ref,
		value,
		autocomplete,
		id,
		name,
	} = props;

	const [showError, setShowError] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleChange = (value?: string): void => {
		onChange?.(value, validateCallback?.(value));
		if (!value) {
			setShowError(false);
		} else {
			validateCallback != null && setShowError(!validateCallback?.(value));
		}
	};

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.addEventListener('focus', () => {
				if (inputRef.current) inputRef.current.style.setProperty('--sq-index', '-1');
			});

			inputRef.current.addEventListener('blur', () => {
				if (!inputRef.current?.value) {
					if (inputRef.current) inputRef.current.style.setProperty('--sq-index', '1');
				}
			});
		}
	}, []);

	return (
		<Flex direction='column' gap={20}>
			<Container>
				<Flex gap={5}>
					{prefix && (
						<Typography size={prefixSize} color={prefixColor} weight={prefixWeight}>
							{prefix}
						</Typography>
					)}
					<StyledInput
						value={value}
						ref={inputRef}
						autoFocus={autoFocus}
						type={type}
						textColor={textColor}
						textSize={textSize}
						textWeight={textWeight}
						inputMode={type === 'number' ? 'numeric' : undefined}
						textAlign={
							textAlign ?? (prefix ? TEXT_ALIGN.LEFT : postfix ? TEXT_ALIGN.RIGHT : TEXT_ALIGN.LEFT)
						}
						placeholder={placeholder}
						onChange={e => handleChange(e.target.value)}
						maxLength={maxlength}
						maxlength={maxlength}
						max={max}
						name={id}
						id={name}
						autoComplete='on'
						autocomplete={autocomplete}
					/>
					<span />
					{postfix && (
						<Wrapper>
							<Typography size={postfixSize} color={postfixColor} weight={postfixWeight}>
								{postfix}
							</Typography>
						</Wrapper>
					)}
				</Flex>
			</Container>
			{showError && (
				<Typography
					size={errorTextSize}
					color={errorTextColor ?? TEXT_COLOR.TEXT6}
					weight={errorTextWeight}
					textAlign={errorTextAlign}
				>
					{errorText}
				</Typography>
			)}
		</Flex>
	);
};
